import React from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faUsers,
	faPencilAlt,
	faCalendarCheck,
	faMapMarkerAlt,
	faClock,
	faPlusCircle,
	faListCheck,
} from "@fortawesome/free-solid-svg-icons";
import '../../../../utils/textareaFix.css';

// Time zone options
const timeZoneOptions = [
	{ value: "America/New_York", label: "Eastern Time (America/New_York)" },
	{ value: "America/Chicago", label: "Central Time (America/Chicago)" },
	{ value: "America/Denver", label: "Mountain Time (America/Denver)" },
	{ value: "America/Los_Angeles", label: "Pacific Time (America/Los_Angeles)" },
	{ value: "America/Anchorage", label: "Alaska Time (America/Anchorage)" },
	{ value: "Pacific/Honolulu", label: "Hawaii Time (Pacific/Honolulu)" },
	{ value: "UTC", label: "Coordinated Universal Time (UTC)" },
];

const CreateEventModal = ({
	show,
	onHide,
	createFormData,
	organizations,
	userRole,
	userOrgID,
	handleCreateFormChange,
	handleCreateSubmit,
	createError,
	createLoading,
	serviceTypes = [], // [{ serviceTypeID, name }]
}) => {
	// Determine if the user is a SuperAdmin (roleID "0") or an OrgAdmin (roleID "1")
	const isSuperAdmin = userRole === "0";

	// For org admins, this should always be their org
	const resolvedOrgId = isSuperAdmin ? createFormData.hostingOrgID : userOrgID;

	const orgName = organizations.find(
		(org) => String(org.orgID) === String(resolvedOrgId),
	)?.name;

	//Ensures serviceTypeIDs is always an array
	const selectedServiceTypeIDs = Array.isArray(createFormData.serviceTypeIDs)
		? createFormData.serviceTypeIDs
		: [];

	// handle checkbox toggles here (keeps your generic handler simple)
	const handleServiceTypeToggle = (serviceTypeID) => {
		const idStr = String(serviceTypeID);

		const next = selectedServiceTypeIDs.includes(idStr)
			? selectedServiceTypeIDs.filter((x) => x !== idStr)
			: [...selectedServiceTypeIDs, idStr];

		// Use your existing handler shape by faking an event
		handleCreateFormChange({
			target: { name: "serviceTypeIDs", value: next },
		});
	};

	return (
		<Modal show={show} onHide={onHide} size="xl" centered>
			<Modal.Header closeButton>
				<Modal.Title>
					<FontAwesomeIcon icon={faPlusCircle} className="me-2" />
					Create New Event
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Form onSubmit={handleCreateSubmit}>
					{createError && <Alert variant="danger">{createError}</Alert>}

					{/* --- Hosting Organization --- */}
					<h4 className="mb-3 text-primary">
						<FontAwesomeIcon icon={faUsers} className="me-2" />
						Hosting Organization
					</h4>
					<div className="row g-3 mb-4">
						<div className="col-md-6">
							<Form.Group className="form-floating">
								{isSuperAdmin ? (
									<Form.Select
										id="createOrgID"
										className="form-select-lg"
										name="hostingOrgID"
										value={createFormData.hostingOrgID}
										onChange={handleCreateFormChange}
										required
										style={{
											height: "2.5rem",
											lineHeight: "2.5rem",
											padding: "0.3rem",
										}}
									>
										<option value="">Select Organization</option>
										{organizations.map((org) => (
											<option key={org.orgID} value={org.orgID}>
												{org.name}
											</option>
										))}
									</Form.Select>
								) : (
									<>
										<Form.Control
											type="text"
											value={orgName || "Your Organization"}
											disabled
											className="fw-semibold text-dark bg-light form-select-lg"
											style={{ padding: "0.75rem 0.5rem" }}
										/>
										<input type="hidden" name="hostingOrgID" value={resolvedOrgId} />
									</>
								)}
								<Form.Label htmlFor="createOrgID" className="visually-hidden">
									Hosting Organization
								</Form.Label>
							</Form.Group>
							{!isSuperAdmin && (
								<Form.Text className="text-muted">
									As an Organization Admin, the event will be associated with your organization.
								</Form.Text>
							)}
						</div>
						<div className="col-md-6"></div>
					</div>

					{/* --- Core Details (tblEvent) --- */}
					<h4 className="mb-3 text-primary">
						<FontAwesomeIcon icon={faPencilAlt} className="me-2" />
						Event Details
					</h4>
					<div className="row g-3 mb-4">
						<div className="col-md-6">
							<Form.Group className="form-floating">
								<Form.Control
									type="text"
									id="createTitle"
									name="title"
									value={createFormData.title}
									onChange={handleCreateFormChange}
									style={{ minHeight: "57px" }}	
									placeholder="Event Title"
									required
								/>
								<Form.Label htmlFor="createTitle">Title</Form.Label>
							</Form.Group>
						</div>

						<div className="col-md-6">
							<Form.Group className="form-floating">
								<Form.Control
									as="textarea"
									id="createDescription"
									name="description"
									value={createFormData.description}
									onChange={handleCreateFormChange}
									style={{ minHeight: "100px" }}	
								/>
								<Form.Label htmlFor="createDescription">Description</Form.Label>
							</Form.Group>
						</div>
					</div>

					{/*Services at Event */}
					<h4 className="mb-3 text-primary">
						<FontAwesomeIcon icon={faListCheck} className="me-2" />
						Services Offered
					</h4>

					<div className="row g-3 mb-4">
						<div className="col-12">
							{serviceTypes.length === 0 ? (
								<Alert variant="warning" className="mb-0">
									No service types are available yet.
								</Alert>
							) : (
								<div className="border rounded p-3 bg-light">
									<div className="d-flex flex-wrap gap-3">
										{serviceTypes.map((st) => {
											const idStr = String(st.serviceTypeID);
											const checked = selectedServiceTypeIDs.includes(idStr);

											return (
												<Form.Check
													key={idStr}
													type="checkbox"
													id={`svc-${idStr}`}
													label={st.name}
													checked={checked}
													onChange={() => handleServiceTypeToggle(idStr)}
												/>
											);
										})}
									</div>

									<Form.Text className="text-muted d-block mt-2">
										Select all services that will be available at this event.
									</Form.Text>
								</div>
							)}
						</div>
					</div>

					{/* --- Schedule Details (tblDay) --- */}
					<h4 className="mb-3 text-primary">
						<FontAwesomeIcon icon={faCalendarCheck} className="me-2" />
						Date & Time
					</h4>
					<div className="row g-3 mb-4">
						<div className="col-md-3">
							<Form.Group className="form-floating">
								<Form.Control
									type="date"
									id="createEffectiveFrom"
									name="effectiveFrom"
									value={createFormData.effectiveFrom}
									onChange={handleCreateFormChange}
									required
								/>
								<Form.Label htmlFor="createEffectiveFrom">Start Date</Form.Label>
							</Form.Group>
						</div>

						<div className="col-md-3">
							<Form.Group className="form-floating">
								<Form.Control
									type="date"
									id="createEffectiveTo"
									name="effectiveTo"
									value={createFormData.effectiveTo}
									onChange={handleCreateFormChange}
									required
								/>
								<Form.Label htmlFor="createEffectiveTo">End Date</Form.Label>
							</Form.Group>
						</div>

						<div className="col-md-3">
							<Form.Group className="form-floating">
								<Form.Control
									type="time"
									id="createStartTime"
									name="startTime"
									value={createFormData.startTime}
									onChange={handleCreateFormChange}
									placeholder="09:00:00"
								/>
								<Form.Label htmlFor="createStartTime">
									<FontAwesomeIcon icon={faClock} className="me-1" />
									Start Time
								</Form.Label>
							</Form.Group>
						</div>

						<div className="col-md-3">
							<Form.Group className="form-floating">
								<Form.Control
									type="time"
									id="createEndTime"
									name="endTime"
									value={createFormData.endTime}
									onChange={handleCreateFormChange}
									placeholder="17:00:00"
								/>
								<Form.Label htmlFor="createEndTime">End Time</Form.Label>
							</Form.Group>
						</div>
					</div>

					<div className="row g-3 mb-4">
						<div className="col-md-4">
							<Form.Group className="form-floating">
								<Form.Select
									className="form-select form-select-floating-fix pb-0 pt-3 ps-3"
									id="createTimeZone"
									name="timeZone"
									value={createFormData.timeZone}
									onChange={handleCreateFormChange}
									required
								>
									<option value="">Select Time Zone</option>
									{timeZoneOptions.map((tz) => (
										<option key={tz.value} value={tz.value}>
											{tz.label}
										</option>
									))}
								</Form.Select>
								<Form.Label htmlFor="createTimeZone">Time Zone</Form.Label>
							</Form.Group>
						</div>

						<div className="col-md-4">
							<Form.Group className="form-floating">
								<Form.Control
									type="date"
									id="createPushDate"
									name="bulletinBoardPushDate"
									value={createFormData.bulletinBoardPushDate}
									onChange={handleCreateFormChange}
									placeholder="YYYY-MM-DD"
								/>
								<Form.Label htmlFor="createPushDate">
									Bulletin Board Push Date
								</Form.Label>
								<Form.Text className="text-muted">
									(Optional: Date when event appears on bulletin board)
								</Form.Text>
							</Form.Group>
						</div>
						<div className="col-md-4"></div>
					</div>

					{/* --- Location Details (tblAddress) --- */}
					<h4 className="mb-3 text-primary">
						<FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
						Location Information
					</h4>
					<div className="row g-3 mb-4">
						<div className="col-md-6">
							<Form.Group className="form-floating">
								<Form.Control
									type="text"
									id="createLocationName"
									name="locationName"
									value={createFormData.locationName}
									onChange={handleCreateFormChange}
									placeholder="e.g., Central Park Pavilion"
									style={{ minHeight: "57px" }}	
								/>
								<Form.Label htmlFor="createLocationName">Location Name</Form.Label>
								<Form.Text className="text-muted">
									(Required: Location name such as "First Baptist")
								</Form.Text>
							</Form.Group>
						</div>

						<div className="col-md-6">
							<Form.Group className="form-floating">
								<Form.Control
									type="text"
									id="createAddressLine1"
									name="addressLine1"
									value={createFormData.addressLine1}
									onChange={handleCreateFormChange}
									placeholder="Address Line 1"
									required
									style={{ minHeight: "57px" }}
								/>
								<Form.Label htmlFor="createAddressLine1">
									Street Address (Line 1)
								</Form.Label>
							</Form.Group>
						</div>

						<div className="col-md-4">
							<Form.Group className="form-floating">
								<Form.Control
									type="text"
									id="createCity"
									name="city"
									value={createFormData.city}
									onChange={handleCreateFormChange}
									placeholder="City"
									required
									style={{ minHeight: "57px" }}
								/>
								<Form.Label htmlFor="createCity">City</Form.Label>
							</Form.Group>
						</div>

						<div className="col-md-4">
							<Form.Group className="form-floating">
								<Form.Select
									id="createState"
									name="state"
									value={createFormData.state}
									onChange={handleCreateFormChange}
									required
									style={{
										minHeight: "57px",
                                        paddingLeft: "1rem",
									}}
									>
									<option value="">Select State</option>
									<option value="AL">Alabama</option>
									<option value="AK">Alaska</option>
									<option value="AZ">Arizona</option>
									<option value="AR">Arkansas</option>
									<option value="CA">California</option>
									<option value="CO">Colorado</option>
									<option value="CT">Connecticut</option>
									<option value="DE">Delaware</option>
									<option value="FL">Florida</option>
									<option value="GA">Georgia</option>
									<option value="HI">Hawaii</option>
									<option value="ID">Idaho</option>
									<option value="IL">Illinois</option>
									<option value="IN">Indiana</option>
									<option value="IA">Iowa</option>
									<option value="KS">Kansas</option>
									<option value="KY">Kentucky</option>
									<option value="LA">Louisiana</option>
									<option value="ME">Maine</option>
									<option value="MD">Maryland</option>
									<option value="MA">Massachusetts</option>
									<option value="MI">Michigan</option>
									<option value="MN">Minnesota</option>
									<option value="MS">Mississippi</option>
									<option value="MO">Missouri</option>
									<option value="MT">Montana</option>
									<option value="NE">Nebraska</option>
									<option value="NV">Nevada</option>
									<option value="NH">New Hampshire</option>
									<option value="NJ">New Jersey</option>
									<option value="NM">New Mexico</option>
									<option value="NY">New York</option>
									<option value="NC">North Carolina</option>
									<option value="ND">North Dakota</option>
									<option value="OH">Ohio</option>
									<option value="OK">Oklahoma</option>
									<option value="OR">Oregon</option>
									<option value="PA">Pennsylvania</option>
									<option value="RI">Rhode Island</option>
									<option value="SC">South Carolina</option>
									<option value="SD">South Dakota</option>
									<option value="TN">Tennessee</option>
									<option value="TX">Texas</option>
									<option value="UT">Utah</option>
									<option value="VT">Vermont</option>
									<option value="VA">Virginia</option>
									<option value="WA">Washington</option>
									<option value="WV">West Virginia</option>
									<option value="WI">Wisconsin</option>
									<option value="WY">Wyoming</option>	
								</Form.Select>
								<Form.Label htmlFor="createState">State</Form.Label>
							</Form.Group>
						</div>

						<div className="col-md-4">
							<Form.Group className="form-floating">
								<Form.Control
									type="text"
									id="createZip"
									name="zip"
									value={createFormData.zip}
									onChange={handleCreateFormChange}
									placeholder="Zip Code"
									required
									style={{ minHeight: "57px" }}
								/>
								<Form.Label htmlFor="createZip">Zip Code</Form.Label>
							</Form.Group>
						</div>
					</div>

					{/* Submit/Cancel Buttons */}
					<div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
						<Button variant="secondary" onClick={onHide} disabled={createLoading}>
							Cancel
						</Button>
						<Button variant="primary" type="submit" disabled={createLoading}>
							{createLoading ? (
								<>
									<Spinner animation="border" size="sm" className="me-2" />
									Creating...
								</>
							) : (
								<>
									<FontAwesomeIcon icon={faPlusCircle} className="me-2" />
									Create Event
								</>
							)}
						</Button>
					</div>
				</Form>
			</Modal.Body>
		</Modal>
	);
};

export default CreateEventModal;
